import styled from 'styled-components';
import { useState } from 'react';
import { LuExpand } from 'react-icons/lu';
import { Form } from 'react-bootstrap';

import SidebarTemplate from '../sidebarElements/SidebarTemplate';
import HexagonAccordion from '../sidebarElements/HexagonAccordion';

import { formatNumber, safePercent } from '@utils/helpers';
import { formatArea, formatLength } from '@utils/measurementSystem';
import { useProject } from '@contexts/Project.context';
import { Button } from 'react-bootstrap';

const LabelDetails = styled.div`
	font-size: 0.9em;
	font-weight: 400;
	p {
		display: grid;
		grid-template-columns: auto 92px;
		row-gap: 6px;

		justify-content: space-between;
		margin: 0;
	}
`;

const Unit = styled.span`
	text-wrap: nowrap;
`;

const Name = styled.span`
	text-transform: capitalize;
	color: ${({ theme }) => theme.colors.textAccent};
	display: block;
`;

const Menu = styled.div`
	color: ${({ theme }) => theme.colors.textColor};
	padding-top: 10px;
	padding-bottom: 20px;
`;

const DetailsName = styled.span`
	text-transform: capitalize;
	display: block;
	font-weight: 500;
`;

const DetailsList = styled.ul`
	list-style: none;
	padding: 0;
	margin: 0;

	font-weight: 400;
	line-height: 1.2;
	color: ${({ theme }) => theme.colors.textColor};

	display: grid;
	grid-template-columns: auto 92px;
	justify-content: space-between;

	margin-top: 4px;
	row-gap: 6px;

	li {
		display: grid;
		grid-template-columns: subgrid;
		grid-column: 1/-1;

		& > span:first-child {
			text-transform: capitalize;
			color: ${({ theme }) => theme.colors.textAccent};
		}
	}
`;

const sidebarId = 'hexagonSummary';

const HexagonSummarySidebar = () => {
	const { hexagonInfo } = useProject();

	const hexagonsSummary = hexagonInfo?.metadata?.total;

	if (!hexagonsSummary?.detections) return;

	return (
		<SidebarTemplate
			zindex={2}
			sidebarId={sidebarId}
			header="Hexagons total summary"
			closeable={true}>
			<Menu>
				<HexagonAccordion hexagonData={hexagonsSummary} />
			</Menu>
		</SidebarTemplate>
	);
};
export default HexagonSummarySidebar;

const SummaryWrapper = styled.div`
	margin: 5px 0 10px;
`;

const MoreButtonWrapper = styled.div`
	margin-top: 14px;
	display: flex;
	justify-content: flex-end;
	align-items: center;
`;

const StemEstimateRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 8px;
	padding: 8px 0;
	border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const StemSelect = styled(Form.Select)`
	width: auto;
	min-width: 100px;
	font-size: 0.85em;
	padding: 4px 24px 4px 8px;
	background-color: ${({ theme }) => theme.colors.modal};
	color: ${({ theme }) => theme.colors.textColor};
	border-color: ${({ theme }) => theme.colors.border};
`;

const StemLabel = styled.span`
	font-size: 0.9em;
	color: ${({ theme }) => theme.colors.textAccent};
`;

// FIX: Area can get to large numbers and force the numbers and units onto a new line (This does not look good and needs to be fixed)
export const HexagonSummaryPreview = () => {
	const { setActiveSidebar, hexagonInfo } = useProject();
	const [stemLevel, setStemLevel] = useState('medium');

	if (!hexagonInfo) return null;

	const hexagonsSummary = hexagonInfo?.metadata?.total;
	const {
		metadata: {
			count,
			area,
			object_area,
			stems_low,
			stems_medium,
			stems_high,
			stems_per_ha_low,
			stems_per_ha_medium,
			stems_per_ha_high,
		} = {},
		detections = [],
	} = hexagonsSummary || {};

	const [areaNumber, areaUnit] = formatArea(area);
	const hasStemEstimates = stems_low > 0 || stems_medium > 0 || stems_high > 0;

	// Get values based on selected level
	const stemValues = {
		low: { stems: stems_low, perHa: stems_per_ha_low },
		medium: { stems: stems_medium, perHa: stems_per_ha_medium },
		high: { stems: stems_high, perHa: stems_per_ha_high },
	};
	const selectedStems = stemValues[stemLevel]?.stems || 0;
	const selectedPerHa = stemValues[stemLevel]?.perHa || 0;

	const showMore = () => {
		setActiveSidebar({ sidebarId, parentId: 'layerView' });
	};

	return (
		<SummaryWrapper>
			<LabelDetails>
				{count && (
					<p>
						<Name>Objects in total</Name>
						<span>{formatNumber(count)}</span>
					</p>
				)}

				{area && (
					<p>
						<Name>Area</Name>
						<span>
							{areaNumber} <Unit>{areaUnit}</Unit>
						</span>
					</p>
				)}

				{hasStemEstimates && (
					<>
						<StemEstimateRow>
							<StemLabel>Stem Estimate</StemLabel>
							<StemSelect
								size="sm"
								value={stemLevel}
								onChange={e => setStemLevel(e.target.value)}>
								<option value="low">Low</option>
								<option value="medium">Medium</option>
								<option value="high">High</option>
							</StemSelect>
						</StemEstimateRow>
						<p>
							<Name>Total Stems</Name>
							<span>{formatNumber(selectedStems, 0)}</span>
						</p>
						<p>
							<Name>Stems/ha</Name>
							<span>
								{formatNumber(selectedPerHa, 0)} <Unit>/ha</Unit>
							</span>
						</p>
					</>
				)}

				{detections &&
					Object.keys(detections).map(key => (
						<DetectionDetail
							key={`hexagon-detail-${key}`}
							name={key}
							detection={detections[key]}
							totalCount={count}
							totalObjectArea={object_area}
						/>
					))}
			</LabelDetails>
			<MoreButtonWrapper>
				<Button
					variant="dark"
					size="sm"
					className="py-1 px-2 d-flex align-items-center justify-content-center gap-2 w-100"
					onClick={showMore}>
					<span>
						<LuExpand /> More Details
					</span>
				</Button>
			</MoreButtonWrapper>
		</SummaryWrapper>
	);
};

const getPercentOfTotal = (count, totalCount) => safePercent(count, totalCount);

// FIX: Sum Object Area can get to large numbers and force the numbers and units onto a new line (This does not look good and needs to be fixed)
const DetectionDetail = ({ name, detection, totalCount, totalObjectArea }) => {
	const { sum_object_area, mean_height, count } = detection ?? {};

	const objectCountPercent = getPercentOfTotal(count, totalCount);
	const objectAreaPercent = getPercentOfTotal(sum_object_area, totalObjectArea);

	const [objectAreaNumber, objectAreaUnit] = formatArea(sum_object_area);

	const [meanHeightNumber, meanHeightUnit] = formatLength(mean_height);

	return (
		<div className="mt-2">
			<DetailsName>{name}</DetailsName>
			<DetailsList>
				<li>
					<span>Count ({objectCountPercent}%)</span>
					<span>{formatNumber(count)}</span>
				</li>
				{sum_object_area && sum_object_area != 0 && (
					<li>
						<span>Sum Object Area ({objectAreaPercent}%)</span>
						<span>
							{objectAreaNumber} <Unit>{objectAreaUnit}</Unit>
						</span>
					</li>
				)}
				<li>
					<span>Mean Height Of Objects</span>
					<span>
						{mean_height != 0 ? (
							<Unit>
								{meanHeightNumber} {meanHeightUnit}
							</Unit>
						) : (
							<>-</>
						)}
					</span>
				</li>
			</DetailsList>
		</div>
	);
};
